// This hook is solely responsible for the management of toasts rendered on a global level
// A toast is added to a queue with varying intervals to be removed
// We hash each toast and clear it from the queue based on its hash

import { ethers } from "ethers";
import { useCallback, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ToastContext } from "../../contexts/ToastContext";
import { ErrorDialogContext } from "../../contexts/ErrorDialogContext";
import { IToast } from "../../interfaces/Toast";
import { formatPotentialContractError } from "../../utils/errors";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { useSFX } from "../useSFX";
import { pickErrorSound } from "../../utils/audio/uiSfx";

function generateToastHash(): string {
  // Generate toast hash and pass into queueing toast
  return ethers.utils.id(new Date().getMilliseconds().toString());
}

export function useToast() {
  // Attach intervals and remove based on toast hash after timeout
  const { toasts, setToasts } = useContext(ToastContext);
  const { showError } = useContext(ErrorDialogContext);
  const { t } = useTranslation();
  const { playSound } = useSFX();

  // We use a reference to toasts so that it will not trigger re-renders
  const toastsRef = useRef<IToast[]>([]);
  toastsRef.current = toasts;

  const removeToast = useCallback((id?: string) => {
    if (id) {
      setToasts(toastsRef.current.filter((toast) => toast.hash !== id));
    }
  }, [setToasts]);

  const addToast = useCallback((toast: IToast, interval?: number | undefined) => {
    const toastHash = generateToastHash();

    const newToast = {
      ...toast,
      interval,
      hash: toastHash,
    };
    setToasts([newToast, ...toasts]);
  }, [setToasts, toasts]);

  const addToasts = useCallback((newToasts: IToast[], interval?: number | undefined) => {
    const updatedNewToasts = newToasts.map((nt) => ({
      ...nt,
      interval,
      hash: generateToastHash(),
    }));
    setToasts([
      ...updatedNewToasts,
      ...toasts
    ]);
  }, [setToasts, toasts]);

  /**
   * Given an error code, displays the related error message in an error dialog
   * @param errorCode Error code returned from API. Should be one of src/i18n/en/errors.json. If doesnt exist, show error code as is
   */
  const addErrorToast = useCallback((
    header: string | JSX.Element,
    errorCode: string,
  ) => {
    const decodedContractError = formatPotentialContractError(errorCode);
    const error =
      decodedContractError ||
      t(`apiErrors:${errorCode}`, {
        defaultValue: errorCode,
      });

    // Pick a sound that matches the error category (insufficient → thud,
    // network → warble, validation → pop, otherwise → buzz). Inspect both
    // the resolved message and the raw code in case the code itself is
    // descriptive (e.g. "INSUFFICIENT_BALANCE").
    const errorSound = pickErrorSound(error || errorCode);
    playSound(errorSound, true);

    // Use error dialog instead of toast for better UX with long error messages
    const headerText = typeof header === "string" ? header : "Error";
    showError(headerText, error);
  }, [playSound, showError, t]);

  return {
    toasts,
    addToast,
    addToasts,
    removeToast,
    addErrorToast,
  };
}
